import { Box, Breadcrumbs, Typography, Link, Divider } from "@mui/joy";
import { ChevronRight } from "lucide-react";
import { useLocation, Link as RouterLink } from "react-router-dom";

interface BreadcrumbItem {
    label: string;
    path: string;
}

/**
 * Builds breadcrumb items based on the current pathname
 */
function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
    const items: BreadcrumbItem[] = [
        { label: "Admin", path: "/admin" },
        { label: "Manufacturers", path: "/admin/manufacture/list" },
    ];

    const segments = pathname.split("/").filter(Boolean);
    const manufactureIndex = segments.indexOf("manufacture");

    if (manufactureIndex === -1 || manufactureIndex === segments.length - 1) {
        return items;
    }

    const actionSegment = segments[manufactureIndex + 1];

    // Add the action (List, Create, Edit)
    if (actionSegment === "create") {
        items.push({ label: "Create", path: pathname });
    } else if (actionSegment && actionSegment !== "list" && actionSegment !== "create") {
        // This is an edit/detail page (has an ID)
        items.push({ label: "Edit", path: pathname });
    }

    return items;
}

export default function Breadcrumb() {
    const location = useLocation();
    const breadcrumbs = buildBreadcrumbs(location.pathname);

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 0, ml: 1, mr: 2 }}>
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <Box key={item.path}>
                            {isLast ? (
                                <Typography level={index === 0 ? "title-lg" : "body-md"} sx={{ fontWeight: 400 }}>
                                    {item.label}
                                </Typography>
                            ) : (
                                <Link
                                    component={RouterLink}
                                    to={item.path}
                                    level={index === 0 ? "title-lg" : "body-md"}
                                    sx={{
                                        fontWeight: 400,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    {item.label}
                                </Link>
                            )}
                        </Box>
                    );
                })}
            </Breadcrumbs>
        </Box>
    );
}
